import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue3-toastify';

import type { InstallationMonorepo } from '~/services/installation-monorepo/installation-monorepo.types';

import installationMonorepoApi from '~/services/installation-monorepo/installation-monorepo';

interface ResetCommissioningParams {
  projectId: string;
  reason: string;
}

const QUERY_KEYS = {
  FINANCING: ['FINANCING'],
  invertersInfo: (projectId: string) => ['inverters-info', projectId],
} as const;

const GENERIC_ERROR_MESSAGE = 'Erro inesperado ao resetar o comissionamento.';

const RESET_COMMISSIONING_ERROR_BY_CODE: Record<
  InstallationMonorepo.ResetCommissioningCode,
  string
> = {
  AMBIGUOUS_PROJECT:
    'Este projeto está ligado a mais de uma instalação. '
    + 'Não é possível resetar automaticamente — abra um chamado para o time técnico.',
  INSTALLATION_NOT_FOUND:
    'Não há instalação vinculada a este projeto. Confira o financiamento ou acione o suporte.',
  MONITORING_NOT_FOUND:
    'Não há comissionamento para resetar nesta instalação. '
    + 'Pode já ter sido resetado ou nunca ter passado pelo fluxo de comissionamento.',
  NO_ELIGIBLE_MONITORING:
    'Nenhum monitoring está elegível para reset. '
    + 'É necessário status de comissionamento em andamento ou com falha.',
};

const getResetCommissioningErrorMessage = (error: unknown): string => {
  const axiosError = error as {
    response?: {
      data?: {
        data?: {
          code?: InstallationMonorepo.ResetCommissioningCode;
          type?: string;
        };
        detail?: string;
        message?: string;
      };
      status?: number;
    };
  };

  const status = axiosError.response?.status;
  const body = axiosError.response?.data;
  const apiMessage = body?.message || body?.detail;
  const code = body?.data?.code;
  const validationType = body?.data?.type;

  if (code && code in RESET_COMMISSIONING_ERROR_BY_CODE) {
    const friendly = RESET_COMMISSIONING_ERROR_BY_CODE[code];

    if (code === 'NO_ELIGIBLE_MONITORING' && apiMessage) {
      return `${friendly} Detalhe: ${apiMessage}`;
    }

    return friendly;
  }

  if (status === 403) {
    return 'Sem permissão para resetar o comissionamento.';
  }

  if (status === 404) {
    return RESET_COMMISSIONING_ERROR_BY_CODE.INSTALLATION_NOT_FOUND;
  }

  if (status === 422 && validationType === 'validation_error') {
    return apiMessage || 'Motivo inválido. Informe entre 10 e 500 caracteres.';
  }

  return apiMessage || GENERIC_ERROR_MESSAGE;
};

export default function useResetCommissioning() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, reason }: ResetCommissioningParams) => (
      installationMonorepoApi.resetCommissioning({ projectId, reason })
    ),
    onError: (error: unknown) => {
      toast.error(getResetCommissioningErrorMessage(error));
    },
    onSuccess: (data, { projectId }) => {
      toast.success(
        `Comissionamento resetado: ${data.removed_monitorings} monitoring(s) `
        + `e ${data.removed_inverters} inversor(es) removidos.`,
      );
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FINANCING });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invertersInfo(projectId) });
    },
    retry: false,
  });
}
